import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { streamDestinationsQueryOptions } from '../../api/streamDestinations';
import {
  Accordion,
  Badge,
  Box,
  Button,
  ScrollAreaRoot,
  ScrollAreaScrollbar,
  ScrollAreaViewport,
  styled,
  Text,
} from '@streamelements/frontend-ui';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Destination,
  DestinationMetadataApiClient,
  DestinationsStorageApiClient,
  XOutputAuxiliaryData,
  YouTubeOutputAuxiliaryData,
} from '../../services/DestinationsApiClient';
import { BadRequestError, UnauthenticatedError } from '../../utils/auth';
import { allowedConnectOptionsSchema, AllowedProvider, platformPrettyName } from '../../constants/common';
import {
  defaultOutputIsEqualToTemplate,
  defaultOutputIsSetUp,
  getTopmostConfiguredOutput,
  setupDefaultToPrimaryOutput,
} from '../_authenticated.index/-defaultCanvasSetup';
import { Fragment } from 'react/jsx-runtime';
import { CompositionsService } from '../../services/CompositionsService';
import { OutputEntry } from './-OutputEntry';
import { promisefy } from '../../utils/promisefy';
import { useState } from 'react';
import { AddOutputInfo, OutputId, OutputInfo } from '../../types/obs';
import { setupStreamRoom, updateStreamRoom } from './-utils';
import { StreamingStart } from '../../services/StreamingStartService';
import { Analytics } from '../../services/AnalyticsService';
import { CreatorIneligibilityError } from '../../types/errors';

const searchParamsSchema = z
  .object({
    output_id: z.string().optional(),
    mode: z.enum(['streams:start', 'streams:update']).optional().default('streams:update'),
  })
  .optional()
  .default({
    mode: 'streams:update',
  });

export const Route = createFileRoute('/_authenticated/settings/_layout/broadcast-settings/')({
  component: ConfigureStreams,
  validateSearch: searchParamsSchema.parse,
  loader: async ({ context: { queryClient, authContextProvider, serviceEnvironment } }) => {
    const destinations = await queryClient.ensureQueryData(
      streamDestinationsQueryOptions({
        authContextProvider,
        serviceEnvironment,
      })
    );

    const compositions = await CompositionsService.getActiveCompositions();
    const streamingStatus = await queryClient.ensureQueryData(StreamingStart.streamingStatusQueryOptions);

    const enabledDestinations = Array.from(destinations)
      .filter(([id, destination]) => {
        void id;
        return new DestinationMetadataApiClient({
          authContextProvider,
          serviceEnvironment,
          destinationMetadata: destination,
        }).isConnected();
      })
      .sort((a, b) => {
        if (a[1].outputs.some((o) => o.auxiliaryData?.isPrimaryOutput)) {
          return -1;
        }

        if (b[1].outputs.some((o) => o.auxiliaryData?.isPrimaryOutput)) {
          return 1;
        }

        return 0;
      });

    const streamingOutputs = (await promisefy(window.host.getAllStreamingOutputs)()) as Record<
      OutputId,
      Destination['outputs'][number]
    >;

    const destinationsStorageApiClient = new DestinationsStorageApiClient({
      authContextProvider,
      serviceEnvironment,
    });

    const primaryOutput = await DestinationsStorageApiClient.getPrimaryOutput();
    const defaultIsSetUp = defaultOutputIsSetUp(streamingOutputs.default as OutputInfo);
    const isDefaultEqualToPrimary = defaultOutputIsEqualToTemplate({
      defaultOutput: streamingOutputs.default as OutputInfo,
      template: primaryOutput,
    });

    const result = {
      destinations,
      compositions,
      enabledDestinations,
      streamingOutputs,
      streamingStatus,
    };

    if (isDefaultEqualToPrimary) {
      return result;
    }

    if (defaultIsSetUp) {
      await destinationsStorageApiClient.unmarkCurrentPrimaryOutput();
      return result;
    }

    if (!primaryOutput) {
      await destinationsStorageApiClient.makePrimaryOutput({
        outputId: enabledDestinations[0][1].outputs[0].id,
      });
    }

    return result;
  },
});

const Root = styled('form', {
  flex: 1,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
});

type BaseProvidersList = readonly [AllowedProvider, ...AllowedProvider[]];

const baseProvidersList: BaseProvidersList = Object.keys(allowedConnectOptionsSchema.Values).filter(
  (provider) => provider !== 'youtube'
) as unknown as BaseProvidersList;

const baseOutputSettingsSchema = z.object({
  id: z.string(),
  provider: z.enum(baseProvidersList),
  category: z.string().optional(),
  title: z.string().optional(),
  categorySearch: z.string().optional(),
  destinationId: z.string(),
  videoCompositionId: z.string().optional(),
  privacyStatus: z.enum(['public', 'unlisted', 'private']).optional().default('public'),
  broadcastId: z.string().optional(),
  youtubeLiveStreamId: z.string().optional(),
  xSourceId: z.string().optional(),
  xChatAccessOption: z.enum(['1', '2', '3', '4', '5']).optional().default('1'),
  scheduledStartTime: z
    .string()
    .default(new Date(Date.now() + 60 * 1000).toISOString())
    .optional(),
});

const youtubeOutputSettingsSchema = z.object({
  ...baseOutputSettingsSchema.shape,
  provider: z.literal('youtube'),
  title: z.string().min(1, 'Title is required'),
});

const outputSettingsSchema = z.discriminatedUnion('provider', [youtubeOutputSettingsSchema, baseOutputSettingsSchema]);

const formSchema = z.object({
  hasPrimaryOutput: z.boolean().default(false),
  outputs: z.record(z.string(), outputSettingsSchema),
});

export type FormValues = z.infer<typeof formSchema>;

const StyledProviderBadge = styled(Badge, {
  boxShadow: '0 0 0 1px rgba(from $$colorText r g b / 0.3)',
  // lineHeight: 1,
  verticalAlign: 'text-bottom',
  pt: '0.25rem',
  mt: '0.125rem',

  variants: {
    color: {
      twitch: {
        // color(from red a98-rgb r g b / alpha)
        backgroundColor: 'rgba(from $colors$twitchPurple r g b / 0.2)',
        $$colorText: '$colors$twitchPurple',
      },
      youtube: {
        backgroundColor: 'rgba(from $colors$youtubeRed r g b / 0.2)',
        $$colorText: '$colors$youtubeRed',
      },
      trovo: {
        // backgroundColor: 'rgba(from $colors$trovoGreen r g b / 0.2)',
        backgroundColor: '$colors$uiSuccess16A',
        $$colorText: '$colors$trovoGreen',
      },
      x: {
        // backgroundColor: 'rgba(from $colors$textPrimaryMain r g b)',
        backgroundColor: '$colors$textPrimary30',
        // $$colorText: '$colors$textPrimaryMainInverted',
        $$colorText: '$colors$textPrimaryMain',
      },
      tiktok: {
        // backgroundColor: 'rgba(from $colors$textPrimaryMain r g b)',
        backgroundColor: '$colors$textPrimary30',
        // $$colorText: '$colors$textPrimaryMainInverted',
        $$colorText: '$colors$textPrimaryMain',
      },
      custom: {
        // backgroundColor: 'rgba(from $colors$textPrimaryMain r g b)',
        backgroundColor: '$colors$textPrimary30',
        // $$colorText: '$colors$textPrimaryMainInverted',
        $$colorText: '$colors$textPrimaryMain',
      },
      kick: {
        backgroundColor: '$colors$textAlwaysDarkMain',
        $$colorText: '$colors$kickGreen',
      },
    },
  },
});

function ProviderBadge({ provider }: { provider: AllowedProvider }) {
  return (
    <StyledProviderBadge appearance="translucent" color={provider}>
      {platformPrettyName[provider]}
    </StyledProviderBadge>
  );
}

function ConfigureStreams() {
  const { output_id } = Route.useSearch();
  const { authContextProvider, serviceEnvironment } = Route.useRouteContext();
  const { destinations, compositions, enabledDestinations } = Route.useLoaderData();
  const { mode = 'streams:update' } = Route.useSearch();
  const { data: streamingStatus } = useSuspenseQuery(StreamingStart.streamingStatusQueryOptions);

  const form = useForm<FormValues>({
    defaultValues: enabledDestinations.reduce(
      (acc, [id, destination]) => {
        void id;

        for (const output of destination.outputs) {
          if (!output.isEnabled && !output.auxiliaryData?.isPrimaryOutput) continue;

          const { streamTitle: title = '', streamCategory: category = destination.provider === 'youtube' ? '20' : '' } =
            output.auxiliaryData || ({} as never);

          const {
            auxiliaryData: {
              streamPrivacyStatus = 'public',
              isPrimaryOutput,
              liveStreamId: youtubeLiveStreamId = 'new',
            },
          } = output as AddOutputInfo<YouTubeOutputAuxiliaryData>;

          const {
            auxiliaryData: { sourceId: xSourceId = 'new', chatAccessOption: xChatAccessOption = '1' },
          } = output as AddOutputInfo<XOutputAuxiliaryData>;

          acc.outputs[output.id] = {
            id: output.id,
            provider: destination.provider,
            title: ['custom'].includes(destination.provider) ? ' ' : title,
            category,
            categorySearch: '',
            destinationId: output.auxiliaryData?.destinationId ?? destination.id,
            videoCompositionId: output.videoCompositionId ?? 'default',
            youtubeLiveStreamId,
            privacyStatus: streamPrivacyStatus,
            xSourceId,
            xChatAccessOption,
            broadcastId: 'new',
          };

          if (isPrimaryOutput) {
            acc.hasPrimaryOutput = true;
          }
        }

        return acc;
      },
      { hasPrimaryOutput: false, outputs: {} } as FormValues
    ),
    mode: 'onSubmit',
    resolver: async (data, ...args) => {
      const result = await zodResolver(formSchema)(data, ...args);
      return result;
    },
  });

  const [expandedOutputs, setExpandedOutputs] = useState<Set<string>>(
    () => new Set(Object.keys(form.getValues('outputs')).map((key) => key))
  );

  const { isValid } = form.formState;

  const createStreamRooms = useMutation({
    mutationKey: ['createStreamRooms'],
    mutationFn: async () => {
      let outputs = form.getValues('outputs');

      if (output_id) {
        outputs = { [output_id]: outputs[output_id] };
      }

      const destinationsStorageApiClient = new DestinationsStorageApiClient({
        authContextProvider,
        serviceEnvironment,
      });

      const templateOutput = await DestinationsStorageApiClient.getPrimaryOutput();
      const failedOutputs: Map<string, Error> = new Map([]);

      for (const outputId in outputs) {
        const output = outputs[outputId];
        const { destinationId } = output;

        const destination = await destinationsStorageApiClient.getDestinationById({
          id: destinationId,
        });

        if (!destination) {
          failedOutputs.set(outputId, new Error('Destination not found'));
          continue;
        }

        const destinationMetadataApiClient = new DestinationMetadataApiClient({
          authContextProvider,
          serviceEnvironment,
          destinationMetadata: destination,
        });

        try {
          const destination = await setupStreamRoom({
            authContextProvider,
            serviceEnvironment,
            destinationMetadataApiClient,
            streamData: output,
          });

          destinations.set(destinationId, destination);
        } catch (error) {
          failedOutputs.set(outputId, error as Error);
        }
      }

      if (streamingStatus.isStreamingActive) {
        return failedOutputs;
      }

      const updatedOutputs = await promisefy(window.host.getAllStreamingOutputs)();
      const initialDefaultIsSetUp = defaultOutputIsSetUp(updatedOutputs.default as OutputInfo);

      let updatedSettings = templateOutput
        ? (updatedOutputs[templateOutput.id] as Destination['outputs'][number])
        : undefined;

      if (!initialDefaultIsSetUp && !updatedSettings) {
        const topmostOutput = await getTopmostConfiguredOutput();

        if (topmostOutput) {
          updatedSettings = {
            ...topmostOutput,
            auxiliaryData: {
              ...topmostOutput.auxiliaryData!,
              isPrimaryOutput: true,
            },
          };

          const destinationsStorageApiClient = new DestinationsStorageApiClient({
            authContextProvider,
            serviceEnvironment,
          });

          await destinationsStorageApiClient.makePrimaryOutput({
            outputId: topmostOutput.id,
          });
        }
      }

      const forceUpdateToPrimaryOutput = form.getValues('hasPrimaryOutput');

      const { isDefaultEqualToPrimary, defaultIsSetUp } = await setupDefaultToPrimaryOutput({
        defaultOutput: updatedOutputs.default,
        primaryOutput: updatedSettings,
        templateOutput,
        forceUpdateToPrimaryOutput,
      });

      if (failedOutputs.size) {
        setExpandedOutputs(new Set(failedOutputs.keys()));
      }

      if (isDefaultEqualToPrimary) {
        return failedOutputs;
      }

      if (!templateOutput?.auxiliaryData?.isPrimaryOutput) {
        return failedOutputs;
      }

      if (defaultIsSetUp) {
        console.info('Default output has been altered, skipping update from primary output');
        console.info('Marking primary output as no longer primary');
        await destinationsStorageApiClient.unmarkCurrentPrimaryOutput();
      }

      return failedOutputs;
    },
    onSuccess: async (failedOutputs) => {
      const successfulOutputsCount = Object.keys(form.getValues('outputs')).length;

      const videoCompositions = await CompositionsService.getActiveCompositions();

      for (const [outputId, output] of Object.entries(form.getValues('outputs'))) {
        if (failedOutputs.has(outputId)) continue;

        const videoComposition = videoCompositions[output.videoCompositionId ?? 'default'];
        const isVertical = videoComposition.videoFrame.width < videoComposition.videoFrame.height;
        const isHorizontal = videoComposition.videoFrame.width > videoComposition.videoFrame.height;

        await Analytics.reportMultiStreamingEvent({
          name: 'streaming_started',
          fields: [
            ['platform', output.provider],
            ['is_using_custom_video_composition', output.videoCompositionId && output.videoCompositionId !== 'default'],
            ['video_composition_orientation', isVertical ? 'vertical' : isHorizontal ? 'horizontal' : 'square'],
          ],
        });
      }

      if (!failedOutputs.size) {
        window.host.endModalDialog(successfulOutputsCount, () => void 0);
      }
    },
  });

  const updateStreamsMetadata = useMutation({
    mutationFn: async () => {
      const outputs = form.getValues('outputs');

      const destinationsStorageApiClient = new DestinationsStorageApiClient({
        authContextProvider,
        serviceEnvironment,
      });

      for (const outputId in outputs) {
        const output = outputs[outputId];

        const destination = await destinationsStorageApiClient.getDestinationById({
          id: output.destinationId,
        });

        if (!destination) continue;

        const destinationMetadataApiClient = new DestinationMetadataApiClient({
          authContextProvider,
          serviceEnvironment,
          destinationMetadata: destination,
        });

        await destinationMetadataApiClient.updateDestination({
          outputs: destinationMetadataApiClient.get().outputs.map((o) => {
            return {
              ...o,
              auxiliaryData: {
                ...o.auxiliaryData!,
                streamTitle: output.title,
                streamCategory: output.category,
                streamPrivacyStatus: output.privacyStatus,
              },
            };
          }),
        });
      }
    },
  });

  const updateStreamRooms = useMutation({
    mutationFn: async () => {
      const outputs = form.getValues('outputs');

      const destinationsStorageApiClient = new DestinationsStorageApiClient({
        authContextProvider,
        serviceEnvironment,
      });

      const destinations = new Map((await destinationsStorageApiClient.getDestinations()).map((d) => [d.id, d]));

      for (const [destinationId] of enabledDestinations) {
        const destination = destinations.get(destinationId);

        if (!destination) {
          continue;
        }

        const destinationMetadataApiClient = new DestinationMetadataApiClient({
          authContextProvider,
          serviceEnvironment,
          destinationMetadata: destination,
        });

        for (const { id: outputId } of destination.outputs) {
          const output = form.getFieldState(`outputs.${outputId}`);
          const value = outputs[outputId];

          if (!output?.isDirty || !value) continue;

          const response = await updateStreamRoom({
            authContextProvider,
            serviceEnvironment,
            destinationMetadataApiClient,
            streamData: value,
          });

          destinations.set(destinationId, response);
        }
      }

      await updateStreamsMetadata.mutateAsync();
    },
  });

  const didCreateAllStreamRooms = createStreamRooms.isSuccess ? createStreamRooms.data?.size === 0 : true;

  return (
    <Root
      onSubmit={(e) => {
        form.handleSubmit(
          () => {
            switch (true) {
              case mode === 'streams:start':
                createStreamRooms.mutate();
                return;
              case mode === 'streams:update' && streamingStatus.isStreamingActive:
                updateStreamRooms.mutate();
                return;
              case mode === 'streams:update' && !streamingStatus.isStreamingActive:
                updateStreamsMetadata.mutate();
                return;
              default:
                throw new Error('Invalid mode');
            }
          },
          (errs) => {
            console.warn({ errs });
          }
        )(e);
      }}
    >
      <ScrollAreaRoot css={{ flex: 1, overflow: 'hidden' }} type="always">
        <ScrollAreaViewport css={{ px: '1.5rem', pt: '0.25rem' }}>
          <FormProvider {...form}>
            <Accordion.Root
              type="multiple"
              value={Array.from(expandedOutputs)}
              onValueChange={(value) => setExpandedOutputs(new Set(value))}
              asChild
            >
              <Box
                css={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem',
                  flex: 1,
                }}
              >
                {enabledDestinations.map(([id, destination]) => {
                  const { outputs } = destination;

                  return (
                    <Fragment key={id}>
                      {outputs.sort((a, b) => {
                        if (a.auxiliaryData.isPrimaryOutput)
                          return -1;
                        else if (b.auxiliaryData.isPrimaryOutput)
                          return 1;
                        else
                        return 0;
                      }).map((output) => {
                        if (!output) return null;

                        if (output_id && output_id !== output.id) return null;

                        if (!output.isEnabled && !output.auxiliaryData?.isPrimaryOutput) return null;

                        if (!output.videoCompositionId || !output.audioCompositionId) return null;

                        return (
                          <OutputEntry
                            output={output}
                            destination={destination}
                            compositions={compositions}
                            key={output.id}
                            authContextProvider={authContextProvider}
                            serviceEnvironment={serviceEnvironment}
                          />
                        );
                      })}
                    </Fragment>
                  );
                })}
              </Box>
            </Accordion.Root>
          </FormProvider>
        </ScrollAreaViewport>
        <ScrollAreaScrollbar />
        <ScrollAreaScrollbar orientation="horizontal" />
      </ScrollAreaRoot>

      {didCreateAllStreamRooms ? null : (
        <Box css={{ px: '1.5rem', pt: '1rem' }}>
          <Text.Body css={{ color: '$textError' }}>Some streams failed to create:</Text.Body>
          <Box
            css={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem',
              py: '1rem',
            }}
          >
            {Array.from(createStreamRooms.data ?? []).map(([outputId, error]) => {
              const outputs = form.getValues('outputs');

              const { destinationId } = outputs[outputId];

              const destination = destinations.get(destinationId);

              if (!destination) {
                return null;
              }

              const errorMessage = (() => {
                switch (true) {
                  case error instanceof BadRequestError:
                    console.warn('BadRequestError', error);
                    return 'Failed to create stream room. Please reconnect your output and try again.';
                  case error instanceof UnauthenticatedError:
                    console.warn('UnauthenticatedError', error);
                    return 'Your output is not connected. Please reconnect your output and try again.';
                  case error instanceof Error:
                    console.warn(error);
                    return error.message;
                  case error instanceof CreatorIneligibilityError:
                    console.warn(error);
                    return (error as CreatorIneligibilityError).message;
                  default:
                    console.warn('default', error);
                    return 'An error occurred';
                }
              })();

              return (
                <Box
                  key={outputId}
                  css={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '0.5rem',
                    bgc: '$uiSecondary',
                    py: '0.5rem',
                    px: '1rem',
                    br: '0.5rem',
                  }}
                >
                  <ProviderBadge provider={destination.provider} /> {destination.displayName}
                  <Text.Body
                    variant="caption"
                    css={{
                      color: '$textAttention',
                      flex: '1 0 100%',
                    }}
                  >
                    {errorMessage ?? (
                      <>
                        The {platformPrettyName[destination.provider]} API responded with:{' '}
                        <strong>{errorMessage ?? 'An unknown error occurred'}</strong>
                      </>
                    )}
                  </Text.Body>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {enabledDestinations.length > 0 && (
        <Box
          css={{
            display: 'flex',
            justifyContent: 'center',
            padding: '1rem',
            gap: '1rem',
          }}
        >
          {/* Button for when the stream is inactive and we're about to start */}
          {mode === 'streams:start' && didCreateAllStreamRooms && (
            <Button type="submit" disabled={!isValid || createStreamRooms.isPending} density="compact">
              {createStreamRooms.isPending ? 'Starting...' : 'Start streaming'}
            </Button>
          )}

          {/* Button for when the stream is active and we're updating titles */}
          {mode === 'streams:update' && didCreateAllStreamRooms && streamingStatus.isStreamingActive && (
            <>
              {updateStreamsMetadata.isSuccess ? (
                <Button type="button" onClick={() => window.host.endModalDialog(1, () => void 0)} density="compact">
                  Continue
                </Button>
              ) : (
                <Button type="submit" disabled={!isValid || updateStreamsMetadata.isPending} density="compact">
                  {updateStreamsMetadata.isPending ? 'Updating...' : 'Update streams'}
                </Button>
              )}
            </>
          )}

          {mode === 'streams:update' && !streamingStatus.isStreamingActive && didCreateAllStreamRooms && (
            <>
              {updateStreamsMetadata.isSuccess ? (
                <Button type="button" onClick={() => window.host.endModalDialog(1, () => void 0)} density="compact">
                  Continue
                </Button>
              ) : (
                <Button type="submit" disabled={!isValid || updateStreamsMetadata.isPending} density="compact">
                  {updateStreamsMetadata.isPending ? 'Updating...' : 'Update settings'}
                </Button>
              )}
            </>
          )}

          {!didCreateAllStreamRooms && (
            <>
              <Button variant="outlined" density="compact" onClick={() => createStreamRooms.mutate()}>
                Try again
              </Button>
              <Button
                density="compact"
                onClick={() => {
                  const successfulOutputs = Object.keys(form.getValues('outputs')).length;
                  const failedOutputs = createStreamRooms.data?.size ?? 0;
                  window.host.endModalDialog(successfulOutputs - failedOutputs, () => void 0);
                }}
              >
                Continue
              </Button>
            </>
          )}
        </Box>
      )}
    </Root>
  );
}
